import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton,
  Input, Table, Thead, Tbody, Tr, Th, Td, Box, Button, Text, Flex,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';

interface EmployeeRaw {
  userId: string;
  userName: string;
  userEmail: string;
  organizationName: string;
}

interface GroupedEmployee {
  userId: string;
  userName: string;
  userEmail: string;
  organizationNames: string[];
}

interface EmployeeSearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (employeeId: string, employeeName: string) => void;
  fetchEmployees: () => Promise<EmployeeRaw[] | undefined>;
}

const EmployeeSearchModal: React.FC<EmployeeSearchModalProps> = ({ isOpen, onClose, onSelect, fetchEmployees }) => {
  const { t } = useTranslation();
  const [filter, setFilter] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [employees, setEmployees] = useState<EmployeeRaw[]>([]);
  const cachedRef = useRef<EmployeeRaw[] | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      setFilter("");
      if (!cachedRef.current) {
        (async () => {
          setIsLoading(true);
          const data = await fetchEmployees();
          if (data) {
            cachedRef.current = data;
            setEmployees(data);
          }
          setIsLoading(false);
        })();
      }
    }
  }, [isOpen]);

  // userIdでグルーピングし、組織名をまとめる
  const groupedEmployees = useMemo(() => {
    const source = cachedRef.current || [];
    const map = new Map<string, GroupedEmployee>();
    source.forEach(emp => {
      const existing = map.get(emp.userId);
      if (existing) {
        if (emp.organizationName && !existing.organizationNames.includes(emp.organizationName)) {
          existing.organizationNames.push(emp.organizationName);
        }
      } else {
        map.set(emp.userId, {
          userId: emp.userId,
          userName: emp.userName,
          userEmail: emp.userEmail || "",
          organizationNames: emp.organizationName ? [emp.organizationName] : [],
        });
      }
    });
    return Array.from(map.values());
  }, [employees]);

  // スペース区切り複数キーワードでリアルタイムフィルタ
  const filteredEmployees = useMemo(() => {
    if (!filter.trim()) return [];
    const filterTerms = filter.trim().toLowerCase().split(/\s+/);
    return groupedEmployees.filter(emp =>
      filterTerms.every(term =>
        (emp.userName || "").toLowerCase().includes(term) ||
        (emp.userEmail || "").toLowerCase().includes(term) ||
        emp.organizationNames.some(name => name.toLowerCase().includes(term))
      )
    );
  }, [filter, groupedEmployees]);

  const handleSelect = (emp: GroupedEmployee) => {
    onSelect(emp.userId, emp.userName);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" initialFocusRef={searchInputRef}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader fontSize="md">{t('components.EmployeeSearchModal.title')}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Flex gap={2} mb={4}>
            <Input
              ref={searchInputRef}
              placeholder={t('components.EmployeeSearchModal.placeholder.search')}
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              size="sm"
              isDisabled={isLoading}
            />
            {filter && (
              <Button size="sm" variant="ghost" onClick={() => setFilter("")} flexShrink={0}>
                {t('common.button.clear')}
              </Button>
            )}
          </Flex>
          <Text fontSize="xs" color="gray.500" mb={1}>{t('components.EmployeeSearchModal.label.searchResult')}: <Text as="span" fontWeight="bold" color="gray.800">{filteredEmployees.length}</Text> {t('components.EmployeeSearchModal.label.count')}</Text>
          <Box maxH="400px" overflowY="auto">
            <Table size="sm" variant="simple" border="1px solid" borderColor="gray.200">
              <Thead position="sticky" top={0} zIndex={1}>
                <Tr bgColor="gray.50">
                  <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.50" textAlign="left">{t('components.EmployeeSearchModal.tableHeader.department')}</Th>
                  <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.50" textAlign="left" w="180px">{t('components.EmployeeSearchModal.tableHeader.name')}</Th>
                </Tr>
              </Thead>
              <Tbody>
                {isLoading ? (
                  <Tr><Td colSpan={2} textAlign="center" color="gray.500" py={8}>{t('common.message.loading')}</Td></Tr>
                ) : !filter.trim() ? (
                  <Tr><Td colSpan={2} textAlign="center" color="gray.500" py={8}>{t('components.EmployeeSearchModal.message.promptSearch')}</Td></Tr>
                ) : filteredEmployees.length === 0 ? (
                  <Tr><Td colSpan={2} textAlign="center" color="gray.500" py={8}>{t('components.EmployeeSearchModal.message.noResult')}</Td></Tr>
                ) : (
                  filteredEmployees.map(emp => (
                    <Tr
                      key={emp.userId}
                      _hover={{ bg: "blue.50", cursor: "pointer" }}
                      onClick={() => handleSelect(emp)}
                    >
                      <Td borderRight="1px solid" borderColor="gray.200" whiteSpace="nowrap" overflow="hidden" textOverflow="ellipsis" title={emp.organizationNames.join(" / ")}>
                        {emp.organizationNames.join(" / ")}
                      </Td>
                      <Td borderRight="1px solid" borderColor="gray.200" whiteSpace="nowrap" overflow="hidden" textOverflow="ellipsis" title={emp.userName}>
                        {emp.userName}
                      </Td>
                    </Tr>
                  ))
                )}
              </Tbody>
            </Table>
          </Box>
        </ModalBody>
        <ModalFooter>
          <Button size="sm" variant="outline" onClick={onClose}>{t('common.button.cancel')}</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default EmployeeSearchModal;
